import React, { useState } from 'react';
import axios from 'axios';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, X, Loader2, FileCheck } from 'lucide-react';

export default function BulkImportModal({ isOpen, onClose, type = 'applicants', onSuccess }) {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [importResult, setImportResult] = useState(null);

    if (!isOpen) return null;

    const titles = {
        users: 'Bulk Import System Users',
        companies: 'Bulk Import Companies',
        applicants: 'Bulk Import Job Applicants',
    };

    const typeLabels = {
        users: 'Users (Admin / Coordinator / Company HR)',
        companies: 'Companies & Auto-created HR Users',
        applicants: 'Job Fair Applicants & Queue Assignment',
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setError('');
            setImportResult(null);
        }
    };

    const handleDownloadSample = () => {
        window.open(`/api/import/sample/${type}`, '_blank');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select an Excel (.xlsx) or CSV file to import.');
            return;
        }

        setLoading(true);
        setError('');
        setImportResult(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(`/api/import/${type}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (res.data.success) {
                setImportResult(res.data);
                if (onSuccess) onSuccess();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Import failed. Please check the file format.');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setError('');
        setImportResult(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
            <div className="w-full max-w-lg glass-card rounded-3xl p-6 sm:p-8 border border-slate-800 shadow-2xl relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Modal Header */}
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                        <FileSpreadsheet className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white">{titles[type] || 'Bulk Data Import'}</h2>
                        <p className="text-xs text-slate-400 mt-0.5">Upload XLSX / CSV file for bulk insertion into {typeLabels[type]}</p>
                    </div>
                </div>

                {/* Step 1: Download Sample Excel Template Button */}
                <div className="mb-6 p-4 rounded-2xl bg-indigo-950/40 border border-indigo-500/20 flex items-center justify-between">
                    <div>
                        <div className="text-xs font-bold text-indigo-300">Step 1: Get Sample File Template</div>
                        <div className="text-[11px] text-slate-400">Download formatted Excel (.xlsx) template for {type}</div>
                    </div>
                    <button
                        type="button"
                        onClick={handleDownloadSample}
                        className="px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md shadow-indigo-600/30 transition-all flex items-center space-x-1.5 shrink-0"
                    >
                        <Download className="w-3.5 h-3.5" />
                        <span>Sample XLSX</span>
                    </button>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="mb-4 p-3.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-xs flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Step 2: Upload File & Import Results */}
                {importResult ? (
                    <div className="space-y-4">
                        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                            <div className="flex items-center space-x-2 font-bold text-sm mb-1">
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                <span>{importResult.message}</span>
                            </div>
                            <div className="text-xs text-slate-300 mt-2 flex space-x-4">
                                <span className="bg-emerald-950 px-2.5 py-1 rounded-lg border border-emerald-500/30 font-semibold">
                                    ✓ Imported: {importResult.success_count}
                                </span>
                                {importResult.skipped_count > 0 && (
                                    <span className="bg-amber-950 px-2.5 py-1 rounded-lg border border-amber-500/30 font-semibold text-amber-300">
                                        ⚠️ Skipped: {importResult.skipped_count}
                                    </span>
                                )}
                            </div>
                        </div>

                        {importResult.errors && importResult.errors.length > 0 && (
                            <div className="max-h-40 overflow-y-auto p-3 rounded-xl bg-slate-900 border border-slate-800 text-[11px] text-amber-400 space-y-1">
                                <div className="font-bold text-slate-300 mb-1">Row Warnings / Errors:</div>
                                {importResult.errors.map((errMsg, idx) => (
                                    <div key={idx}>• {errMsg}</div>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-end space-x-3 pt-2">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold"
                            >
                                Import Another File
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-xs font-bold text-slate-300 mb-2">
                                Step 2: Upload Filled Excel (.xlsx / .csv) File
                            </label>
                            <div className="relative border-2 border-dashed border-slate-700 rounded-2xl p-6 text-center hover:border-emerald-500/50 transition-colors bg-slate-900/60">
                                <input
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                {file ? (
                                    <div className="flex flex-col items-center">
                                        <FileCheck className="w-10 h-10 text-emerald-400 mb-2" />
                                        <div className="text-sm font-bold text-white">{file.name}</div>
                                        <div className="text-xs text-slate-400 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                        <div className="text-xs font-bold text-slate-200">Click to select or drag & drop file</div>
                                        <div className="text-[11px] text-slate-500 mt-1">Supports Excel (.xlsx, .xls) and CSV (.csv)</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-3 border-t border-slate-800">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={loading || !file}
                                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/30 transition-all flex items-center space-x-2 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Importing Data...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        <span>Start Bulk Import</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
